import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button.tsx';
import { Link, useNavigate } from 'react-router';
import { PlantSchema, TPlant } from '@/features/plants/schemas/plants.schema.ts';
import { CreatePlantFormNavigator } from '@/features/plants/components/plant-management/shared/form-navigator.tsx';
import { Suspense, useEffect } from 'react';
import { LoadingSpinner } from '@/components/ui/loading-spinner.tsx';
import { useGetPlantQuery } from '@/features/plants/hooks/queries/[plantId]/use-get-plant.ts';
import { usePatchPlantQuery } from '@/features/plants/hooks/mutations/[plantId]/use-patch-plant.ts';
import { useGetPlantDevicesQuery } from '@/features/plants/hooks/queries/[plantId]/use-get-devices-plant.ts';
import { STEPS_EDIT } from './plant-form-edit-steps.tsx';
import { hasPermission } from '@/features/users/schemas/users.permissions.ts';
import { useSession } from '@/services/providers/session-provider.tsx';

export const EditPlantForm = ({ plantId }: { plantId: string }) => {
  const navigate = useNavigate();
  const { userId, role } = useSession();

  const { data, isLoading } = useGetPlantQuery({ plantId });

  const { data: plantDevices, isLoading: isDevicesLoading } = useGetPlantDevicesQuery({
    plantId,
  });

  const form = useForm({
    resolver: zodResolver(PlantSchema),
    mode: 'onChange',
    defaultValues: {
      plantId: data?.plantId ?? '',
      companyId: data?.plantId ?? '',
      basicDetails: {
        name: data?.basicDetails.name ?? '',
        type: data?.basicDetails.type ?? '',
        status: data?.basicDetails.status ?? 'Maintenance',
        shortDescription: data?.basicDetails.shortDescription ?? '',
      },
      siteInformation: {
        image: data?.siteInformation?.image ?? '',
        country: {
          label: '',
          value: '',
        },
        state: {
          label: '',
          value: '',
        },
        city: {
          label: '',
          value: '',
        },
        coordinates: {
          latitude: '',
          longitude: '',
        },
        siteAddress: '',
        siteArea: '',
        siteAccess: {
          label: '',
          value: '',
        },
      },
      technicalSpecifications: {
        setupType: data?.technicalSpecifications.setupType,
      },
      equipmentAndConnectionDetails: {
        devices: plantDevices ?? [],
      },
      financialInformation: {
        tariffRate: '',
        expectedROI: '',
        operationalCosts: '',
      },
      contactDetails: {
        owner: {
          firstName: '',
          lastName: '',
        },
        ownerEmail: '',
        ownerPhoneNumber: '',
        installer: '',
        installerEmail: '',
        installerPhoneNumber: '',
      },
      dataCollectionInterval: data?.dataCollectionInterval ?? '',
      createdAt: data?.createdAt ?? '',
      updatedAt: data?.updatedAt ?? '',
    },
  });

  useEffect(() => {
    if (data) {
      form.reset({
        ...data,
        equipmentAndConnectionDetails: {
          devices: plantDevices ?? [],
        },
      });
      form.trigger('equipmentAndConnectionDetails.devices');
    }
  }, [isLoading, isDevicesLoading]);

  const updatePlantMutation = usePatchPlantQuery({
    onSuccess: () => {
      navigate('/plants');
    },
  });

  const onSubmit = (data: TPlant) => {
    updatePlantMutation.mutate(data);
  };

  if (!data?.plantId && !isLoading)
    return (
      <div>
        Oops... No plant found with the id of <b>{plantId}</b>
      </div>
    );

  return (
    <div className="@container">
      <div className="@-2xl:grid-cols-[1fr_250px] block w-full max-w-7xl items-start gap-2 pb-10 sm:flex @2xl:gap-4">
        <div className="flex flex-1 flex-col gap-6">
          <div className="flex flex-col gap-2">
            <h2 className="text-2xl font-bold">Edit Plant</h2>
          </div>
          <div className="">
            <FormProvider {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)}>
                <div className="flex flex-col gap-4">
                  {isLoading && (
                    <div className="flex h-20 items-center justify-center">
                      <LoadingSpinner size={50} />
                    </div>
                  )}
                  {!isLoading && (
                    <Suspense
                      fallback={
                        <div className="flex min-h-svh justify-center">
                          <LoadingSpinner size={50} />
                        </div>
                      }
                    >
                      {STEPS_EDIT.map((S) => (
                        <S.Element key={S.id} showTitle={true} />
                      ))}
                    </Suspense>
                  )}

                  <div className="flex items-center justify-end gap-2 sm:gap-4">
                    <Button
                      disabled={updatePlantMutation.isPending}
                      variant="ghost"
                      type="button"
                      asChild
                    >
                      <Link to="/plants">Cancel</Link>
                    </Button>

                    {hasPermission({ userId, role }, 'plants', 'update') && (
                      <Button
                        disabled={updatePlantMutation.isPending}
                        variant="secondary"
                        type="button"
                        onClick={() =>
                          form.reset({
                            ...data,
                            equipmentAndConnectionDetails: {
                              devices: plantDevices ?? [],
                            },
                          })
                        }
                      >
                        Reset
                      </Button>
                    )}

                    {hasPermission({ userId, role }, 'plants', 'update') && (
                      <Button
                        disabled={updatePlantMutation.isPending}
                        type="button"
                        onClick={() => {
                          form.handleSubmit(onSubmit)();
                        }}
                      >
                        Submit
                      </Button>
                    )}
                  </div>
                </div>
              </form>
            </FormProvider>
          </div>
        </div>
        <div className="h-full">
          <div className="sticky top-0">
            <CreatePlantFormNavigator steps={STEPS_EDIT} currentStepIndex={6} />
          </div>
        </div>
      </div>
    </div>
  );
};
